import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';

import { useTokenPairStore } from '@/features/tokenPairs';
import { useSpeedBackend } from '@/features/wallet';
import { queryKeys } from '@/lib/query';

import {
    selectHasRealOpenTrade,
    selectHasRestingOrder,
    useTradingStore,
} from '../store';
import { estimateLiquidationPrice } from '../utils/pnl';

import { useMaintenanceRate } from './useMaintenanceRate';

const QUOTE_DEBOUNCE_MS = 400;
const QUOTE_STALE_TIME_MS = 5_000;
const QUOTE_REFETCH_MS = 10_000;
const MIN_ORDER_VALUE_USD = 10;

export interface TradeQuote {
    /** Notional position size in USD (collateral × leverage). */
    notionalUsd: number | null;
    /** Position size in asset units. */
    sizeAsset: number | null;
    /** Slippage-padded order value in USD. */
    orderValueUsd: number | null;
    /** Best estimate of the price the order actually fills at: the live mark.
     *  This — NOT `limitPx` — is what the UI should call an entry price, and
     *  what TP/SL targets must be derived from. */
    markPx: number | null;
    /** Worst price the order may fill at, per direction: mark ± the slippage
     *  tolerance. Long pads up, short pads down. */
    slipCapLong: number | null;
    slipCapShort: number | null;
    /** The backend's slippage-padded limit price for the LONG side.
     *
     *  NOT an entry estimate — it is `mark × (1 + slippage)`, i.e. the worst
     *  fill the order will accept, and it is long-only because the quote is
     *  requested with `isLong: true`. Rendering it as "Est. entry price"
     *  overstated the entry by the whole slippage tolerance (1% by default) and
     *  pointed the wrong way for shorts; it also skewed every TP target derived
     *  from it. Kept exposed as the backend's authoritative figure — use
     *  `markPx` / `slipCap*` for display. */
    limitPx: number | null;
    /** Estimated liquidation price for a LONG at the current mark (pre-trade). */
    estLiqLong: number | null;
    /** Estimated liquidation price for a SHORT at the current mark (pre-trade). */
    estLiqShort: number | null;
    /** True while the authoritative backend quote is loading. */
    isLoading: boolean;
    /** True when sizing came from the backend rather than a local estimate. */
    isBackedByQuote: boolean;
}

function useDebouncedValue<T>(value: T, delayMs: number): T {
    const [debounced, setDebounced] = useState(value);
    useEffect(() => {
        const id = setTimeout(() => setDebounced(value), delayMs);
        return () => clearTimeout(id);
    }, [value, delayMs]);
    return debounced;
}

/**
 * Pre-trade order preview.
 *
 * Split by what each figure depends on:
 *   - SIZING (size, notional, order value) comes from `POST /speed/quote` in
 *     real mode — the backend owns lot-size rounding — with a client-side
 *     estimate as the instant fallback before the quote resolves and in demo
 *     mode. Refreshed on an interval; see the query below.
 *   - PRICES (mark, slippage caps, liquidation estimates) are ALWAYS derived
 *     client-side from the streaming mark, so they track the feed instead of
 *     the quote's refresh cadence.
 *
 * That split is deliberate: the panel previously read the quote's `limitPx` as
 * its entry price, which was both the wrong number (a slippage cap, ~1% off)
 * and a frozen one (nothing refetched the quote), while the liquidation prices
 * beside it kept moving.
 */
export function useTradeQuote(currentValue: number): TradeQuote {
    // quote read moved to factions.backend's speed facet (V1).
    const backend = useSpeedBackend();
    const isRealMode = useTradingStore((s) => s.runtimeMode === 'real');
    const betAmount = useTradingStore((s) => s.betAmount);
    const leverage = useTradingStore((s) => s.leverage);
    const slippageBps = useTradingStore((s) => s.slippageBps);
    const selectedCoin = useTokenPairStore((s) => s.selectedCoin);
    // Both panels swap the order form out for the position view while a trade
    // is live (SpeedTradeRail's `showCloseView`), so the preview this quote
    // feeds is unmounted — polling for it would be pure waste.
    // ...and the resting card, which replaces the same form: a quote nobody can
    // see is pure waste.
    const hasOpenTrade = useTradingStore(
        (s) =>
            selectHasRealOpenTrade(s) ||
            selectHasRestingOrder(s) ||
            s.activePosition !== null,
    );

    const debouncedCollateral = useDebouncedValue(betAmount, QUOTE_DEBOUNCE_MS);

    const notionalUsd = betAmount > 0 ? betAmount * leverage : null;
    // Where the exchange would actually liquidate this order — the naive
    // 1/leverage form drew it up to twice as far away as it really is.
    const maintenanceRate = useMaintenanceRate(notionalUsd);
    const localSize =
        notionalUsd !== null && currentValue > 0
            ? notionalUsd / currentValue
            : null;

    const quoteEnabled =
        isRealMode &&
        !hasOpenTrade &&
        !!backend &&
        !!selectedCoin &&
        debouncedCollateral > 0 &&
        debouncedCollateral * leverage >= MIN_ORDER_VALUE_USD;

    const query = useQuery({
        queryKey: queryKeys.speed.quote(
            selectedCoin,
            leverage,
            debouncedCollateral,
            slippageBps,
        ),
        queryFn: async () =>
            backend!.quoteOpen({
                coin: selectedCoin,
                isLong: true,
                collateralUsd: debouncedCollateral,
                leverage,
                slippageBps,
            }),
        enabled: quoteEnabled,
        staleTime: QUOTE_STALE_TIME_MS,
        // The key is (coin, leverage, collateral, slippage) — deliberately NOT
        // the price, so a moving market doesn't spawn a cache entry per tick.
        // The cost of that is a quote which would otherwise never refresh while
        // the user sits on the form: `staleTime` only MARKS an entry stale,
        // something still has to trigger the refetch. Without this the
        // backend-sized rows (size / order value) froze at whatever the market
        // was when the amount was last edited. Price-derived display values are
        // computed from the live mark below, so this interval only has to keep
        // the lot-size rounding honest — hence 10s rather than per-tick.
        //
        // `quoteEnabled` excludes the open-position case, so this never polls
        // for a preview the user cannot see. React Query also pauses the
        // interval on a hidden tab (refetchIntervalInBackground defaults off).
        refetchInterval: quoteEnabled ? QUOTE_REFETCH_MS : false,
        // A sub-$10 order or transient validation error shouldn't spam retries;
        // we just fall back to the local estimate.
        retry: false,
    });

    return useMemo(() => {
        const q = query.data ?? null;
        // Every price-derived figure below is computed from the LIVE mark, not
        // from the cached quote: the quote refreshes on an interval, the feed
        // ticks continuously, and a frozen entry price sitting next to moving
        // liquidation prices is what made the panel look broken.
        const markPx = currentValue > 0 ? currentValue : null;
        const slipFrac = slippageBps / 10_000;
        const estLiqLong =
            markPx !== null
                ? estimateLiquidationPrice(
                      markPx,
                      'LONG',
                      leverage,
                      maintenanceRate,
                  )
                : null;
        const estLiqShort =
            markPx !== null
                ? estimateLiquidationPrice(
                      markPx,
                      'SHORT',
                      leverage,
                      maintenanceRate,
                  )
                : null;

        return {
            notionalUsd: q?.notionalUsd ?? notionalUsd,
            sizeAsset: q?.sizeAsset ?? localSize,
            orderValueUsd: q?.orderValueUsd ?? notionalUsd,
            markPx,
            // matches how the backend derives limitPx (verified against a live
            // quote: limitPx / (1 + slip) lands on the mark to the cent), just
            // resolved per direction instead of long-only.
            slipCapLong: markPx !== null ? markPx * (1 + slipFrac) : null,
            slipCapShort: markPx !== null ? markPx * (1 - slipFrac) : null,
            limitPx: q?.limitPx ?? null,
            estLiqLong,
            estLiqShort,
            isLoading: quoteEnabled && query.isLoading,
            isBackedByQuote: q !== null,
        };
    }, [
        query.data,
        query.isLoading,
        quoteEnabled,
        notionalUsd,
        localSize,
        currentValue,
        leverage,
        slippageBps,
        maintenanceRate,
    ]);
}
